import { ALLOWED_PARENTS } from '@/env';

/** Messages the embedded player posts up to its embedding host (web account or mobile WebView). */
export type EmbedMessage =
  | { type: 'bozp:completed'; hazardProfileId: string; score: number }
  | { type: 'bozp:close' };

/** react-native-webview injects this bridge object into the page. */
declare global {
  interface Window {
    ReactNativeWebView?: { postMessage: (data: string) => void };
  }
}

/** True when the player is running inside the supported web-account iframe. */
export function isWebEmbed(embed: string | null): boolean {
  return embed === 'web';
}

/** True when the player is running inside the FixIt app's in-app WebView. */
export function isMobileEmbed(embed: string | null): boolean {
  return embed === 'mobile';
}

/** True when the player is embedded in ANY host that consumes its postMessages. */
export function isEmbedded(embed: string | null): boolean {
  return isWebEmbed(embed) || isMobileEmbed(embed);
}

/**
 * Post a message to the embedding host. Two channels:
 *
 * - `embed=web` — the framing web account. Posts to `window.parent`, but ONLY when
 *   `parentOrigin` is an explicitly allowlisted origin (never '*'), so the player
 *   can't be coaxed into messaging an arbitrary framer.
 * - `embed=mobile` — the FixIt app's in-app WebView. Posts through the
 *   `window.ReactNativeWebView` bridge (JSON string). No origin gate applies: the
 *   bridge is injected by, and only reaches, the hosting native app.
 *
 * Returns false (no-op) when not embedded, the channel is unavailable, or the web
 * origin is not allowed; never throws.
 */
export function postToParent(
  embed: string | null,
  parentOrigin: string | null,
  message: EmbedMessage,
): boolean {
  if (isMobileEmbed(embed)) {
    const bridge = window.ReactNativeWebView;
    if (!bridge) return false;
    bridge.postMessage(JSON.stringify(message));
    return true;
  }
  if (!isWebEmbed(embed) || !parentOrigin) return false;
  if (!ALLOWED_PARENTS.includes(parentOrigin)) {
    // Never drop this silently: a framing origin that CSP `frame-ancestors`
    // permits but `VITE_BOZP_ALLOWED_PARENTS` omits produces a player that
    // loads fine and then answers nothing — the host's close/complete buttons
    // look dead. Make the misconfiguration visible in the console.
    console.error(
      `BOZP embed message dropped: parentOrigin "${parentOrigin}" is not in VITE_BOZP_ALLOWED_PARENTS`,
      { message: message.type, allowed: ALLOWED_PARENTS },
    );
    return false;
  }
  // targetOrigin is the validated parentOrigin so the message can only reach that frame.
  window.parent.postMessage(message, parentOrigin);
  return true;
}
